import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u2_x4_grundfoss_magna';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einleitung',
    body: 'Die Grundfos MAGNA3 ist eine intelligente Hocheffizienz-Nassläuferpumpe für Heizungs-, Kälte- und Zirkulationsanwendungen in größeren Wohn-, Gewerbe- und Industriegebäuden. Sie bietet maximale Energieeffizienz, vielfältige Regelungsarten und umfassende Kommunikationsmöglichkeiten.'
  },
  {
    heading: 'Herausragende Merkmale',
    body: '•   Höchste Energieeffizienz: EC-Motortechnologie, oft bester EEI-Wert ihrer Klasse.\n•   Intelligente Regelungsarten: AUTOADAPT, FLOWADAPT (AUTOADAPT + FLOWLIMIT), Proportionaldruck, Konstantdruck, Konstanttemperatur (Sensor nötig).\n•   Integrierte Wärmemengenzählung: Erfasst und zeigt den Energieverbrauch an.\n•   TFT-Farbdisplay & intuitive Bedienung: Einfache Konfiguration und Überwachung.\n•   Trockenlaufschutz und Motorvollschutz.'
  },
  {
    heading: 'Kommunikation und Konnektivität',
    body: 'Standard:\n•   Digitale Ein-/Ausgänge\n•   Analog-Eingang (0-10V)\n•   Relais (Störmeldung)\n\nGrundfos GO Remote App:\n•   Drahtlose Bedienung und Überwachung via Bluetooth (mit MI 401 Dongle).\n\nOptional über CIM-Module:\n•   BACnet, Modbus, LONworks, PROFIBUS, PROFINET, GSM/GPRS.'
  },
  {
    heading: 'Anwendungsbereiche und Installation',
    body: '•   Heizungssysteme (Einrohr, Zweirohr, Fußbodenheizung)\n•   Klimaanlagen und Kälteanlagen\n•   Solare und geothermische Systeme\n•   Brauchwarmwasserzirkulation\n\nAnschluss:\n•   Elektrisch: 1~230V oder 3~400V, Klemmkasten.\n•   Hydraulisch: Gewinde- oder Flanschanschluss (DN 25-100).\n•   Einbaulage: Motorwelle horizontal.'
  },
  {
    heading: 'Worauf beim Einsatz achten?',
    body: '•   Präzise Auslegung der Pumpe für die spezifische Anwendung.\n•   Auswahl der optimalen Regelungsart (AUTOADAPT oft eine gute Wahl).\n•   Notwendigkeit von externen Sensoren (z.B. Temperaturfühler) und CIM-Modulen für Gebäudeleittechnik prüfen.\n•   Korrekte Einbaulage (Motorwelle horizontal).'
  }
];

const images = [
  { id: 'img_magna_pumpe', label: 'MAGNA3 Pumpe', src: `${IMAGE_BASE}pumpe_magna3.webp`, alt: 'Grundfos MAGNA3 mit Display' },
  { id: 'img_magna_bed', label: 'Bedienfeld', src: `${IMAGE_BASE}pumpe_magna3_bed.webp`, alt: 'MAGNA3 Bedienfeld' },
  { id: 'img_magna_einbau', label: 'Einbau', src: `${IMAGE_BASE}pumpe_magna3_einbau.webp`, alt: 'MAGNA3 Einbau' },
  { id: 'img_magna_remote', label: 'GO Remote App', src: `${IMAGE_BASE}pumpe_remote.webp`, alt: 'Grundfos GO Remote App' },
  { id: 'img_magna_anschluss', label: 'Anschlussfeld', src: `${IMAGE_BASE}pumpe_magna3_anschlussfeld.webp`, alt: 'MAGNA3 Anschlussfeld' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.2.4 Grundfos MAGNA3 – Intelligente Hocheffizienzpumpe', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u2_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


